import { type ReactNode, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { buttonVariants } from "~/atoms/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/atoms/alert-dialog";
import { type DocumentEditFormValues } from "~/hooks/useDocumentEditForm";
import { type AlertData, DocumentFieldsContext } from "./DocumentFieldsContext";

interface DocumentFieldsGuardProps {
  children: ReactNode;
}

export const DocumentFieldsGuard = ({ children }: DocumentFieldsGuardProps) => {
  const { t } = useTranslation();
  const form = useFormContext<DocumentEditFormValues>();

  const [selectedParentDocuments, selectedChildDocuments] = useWatch({
    control: form.control,
    name: ["parentDocuments", "childDocuments"],
  });

  const [alertData, setAlertData] = useState<AlertData>();

  const handleWarningConfirm = (data: AlertData) => {
    form.setValue(data.type, data.newValue);
    form.setValue("parentDocuments", []);
    form.setValue("childDocuments", []);
  };

  const setClassificationValueWithGuard = (data: AlertData) => {
    if (
      selectedParentDocuments.length > 0 ||
      selectedChildDocuments.length > 0
    ) {
      setAlertData(data);
    } else {
      handleWarningConfirm(data);
    }
  };

  return (
    <DocumentFieldsContext.Provider
      value={{ setDocumentValueWithGuard: setClassificationValueWithGuard }}
    >
      {children}
      <AlertDialog
        open={alertData !== undefined}
        onOpenChange={(open) => {
          if (!open) setAlertData(undefined);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {t(($) => $.modal.removeLinksWarning.title)}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {t(($) => $.modal.removeLinksWarning.description)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md bg-gray-50 p-3">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">
                {t(($) => $.modal.removeLinksWarning.linkedDocumentsRemoval)}
              </h4>
              <ul className="list-disc space-y-1 pl-4 text-sm">
                {selectedParentDocuments
                  .concat(selectedChildDocuments)
                  .map((d) => (
                    <li key={d.value} className="text-gray-700">
                      {d.label}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(($) => $.commons.buttons.cancel)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                alertData ? handleWarningConfirm(alertData) : undefined
              }
              className={buttonVariants({ variant: "destructive" })}
            >
              {t(($) => $.modal.removeLinksWarning.proceed)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DocumentFieldsContext.Provider>
  );
};
